/**
 * Packaging OS — Login (MM2: internal auth cutover).
 *
 * Email/password sign-in against the app-owned `/api/auth/login`
 * endpoint. The historical Core-OAuth redirect
 * (VITE_CORE_AUTH_START_URL) is REMOVED — Packaging OS no longer
 * depends on the central core for login. Google sign-in is shown as an
 * honestly-disabled option until the internal (Packaging-OS-owned)
 * OAuth flow ships in a later phase.
 *
 * Credential handling: the form posts over the API client; the session
 * arrives as an HttpOnly cookie set by the backend. No password or
 * token is ever stored in JS state beyond the controlled form fields.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuthMe, getGoogleAuthStatus, postLogin, type ApiResult, type LoginResponse } from "../api/client";
import { Button } from "../components/Button";
import { Footer } from "../components/Footer";
import { PublicHeader } from "../components/PublicHeader";
import { StatusPill } from "../components/StatusPill";
import { TrustBanner } from "../components/TrustBanner";
import { findLocale, type LocaleCode } from "../i18n/locales";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

type LoginState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "preview-notice" }
  | { kind: "error"; message: string };

export function LoginPage(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);
  const navigate = useNavigate();
  const [googleEnabled, setGoogleEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void getGoogleAuthStatus().then((r) => {
      if (!cancelled && r.outcome === "ok") setGoogleEnabled(Boolean(r.data?.enabled));
    });
    return () => { cancelled = true; };
  }, []);
  const { locale: localeParam } = useParams<{ locale?: string }>();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<LoginState>({ kind: "idle" });

  // Resolve the locale prefix for the post-login redirect.
  const routeLocale: LocaleCode = (() => {
    const found = localeParam ? findLocale(localeParam) : undefined;
    return found ? found.code : locale;
  })();
  const localePrefix =
    localeParam && findLocale(localeParam) ? `/${routeLocale}` : "";
  const appUrl = `${localePrefix}/app`;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (state.kind === "submitting") return;
    setState({ kind: "submitting" });

    const result: ApiResult<LoginResponse> = await postLogin(
      email.trim(),
      password,
    );
    if (result.outcome === "ok" && result.data?.ok) {
      // Session cookie is set. Check preview status before entering.
      const me = await getAuthMe();
      if (me.outcome === "ok" && me.data?.is_preview) {
        setState({ kind: "preview-notice" });
        return;
      }
      navigate(appUrl, { replace: true });
      return;
    }
    switch (result.outcome) {
      case "auth-missing":
      case "auth-invalid":
        setState({ kind: "error", message: t.loginErrorInvalidCredentials });
        break;
      case "permission-denied":
        setState({ kind: "error", message: t.loginErrorSuspended });
        break;
      default:
        setState({ kind: "error", message: t.loginErrorUnavailable });
    }
  }

  const submitting = state.kind === "submitting";

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[{ to: "/", label: t.ctaBackToLanding }]}
      />

      <main className="po-page-main po-page-main--form">
        <TrustBanner message={t.trustBannerFull} />

        <div className="po-login" aria-labelledby="po-login-title">
          <h1 id="po-login-title" className="po-login__title">
            {t.loginTitle}
          </h1>

          {state.kind === "preview-notice" ? (
            <div className="po-stack" role="status" data-testid="login-preview-notice">
              <StatusPill tone="info" label={t.accessModePreview} />
              <p className="po-note">{t.loginPreviewModeNotice}</p>
              <Button variant="primary" onClick={() => navigate(appUrl)}>
                {t.loginPreviewContinue}
              </Button>
            </div>
          ) : (
            <form
              className="po-stack"
              onSubmit={handleSubmit}
              data-testid="login-email-form"
            >
              <label className="po-field">
                <span className="po-field__label">{t.loginEmailLabel}</span>
                <input
                  className="po-field__input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                />
              </label>
              <label className="po-field">
                <span className="po-field__label">{t.loginPasswordLabel}</span>
                <input
                  className="po-field__input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
              </label>

              {state.kind === "error" && (
                <p
                  className="po-note"
                  role="alert"
                  data-testid="login-error-notice"
                  style={{ color: "var(--color-warning-fg)" }}
                >
                  {state.message}
                </p>
              )}

              <Button
                variant="primary"
                type="submit"
                disabled={submitting}
                aria-disabled={submitting}
                data-testid="login-submit-button"
              >
                {submitting ? t.loginSubmittingLabel : t.loginSubmitLabel}
              </Button>
            </form>
          )}

          <hr
            style={{
              border: 0,
              borderTop:
                "var(--border-width-hairline) solid var(--color-border-subtle)",
              margin: 0,
            }}
          />

          {/* Google sign-in — honestly disabled until the internal
              (Packaging-OS-owned) OAuth flow ships. No core redirect. */}
          <Button
            variant="secondary"
            disabled={!googleEnabled}
            aria-disabled={!googleEnabled}
            title={googleEnabled ? undefined : t.loginGoogleUnavailableNotice}
            data-testid="login-google-button"
            onClick={() => {
              if (googleEnabled) window.location.href = "/api/auth/google/start";
            }}
          >
            {t.ctaSignInWithGoogle}
          </Button>
          {!googleEnabled ? (
            <p
              className="po-note po-note--muted po-note--small"
              data-testid="login-google-unavailable-notice"
            >
              {t.loginGoogleUnavailableNotice}
            </p>
          ) : null}

          <p className="po-note">{t.loginRequestAccessLead}</p>
          <Button variant="ghost" onClick={() => navigate("/preview")}>
            {t.ctaRequestAccess}
          </Button>

          <p className="po-note po-note--muted po-note--small">
            {t.loginTrustParagraph}
          </p>
        </div>
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
